import { useSyncExternalStore } from "react";
import { downloadOfflineGame, fetchDownloadableGames, removeOfflineGame } from "./downloadGame";
import { userIdFromToken } from "./gameFiles";
import { listOfflineGames } from "./registry";
import { isOfflinePlaySupported } from "./serviceWorker";

// One place for offline-game state, shared by Settings → Played Games, the
// post-play prompt and the "available offline" dots:
//   downloaded  games stored on this device (registry)
//   downloads   in-progress or failed downloads: { status: "downloading" | "failed", progress, error }
//   catalog     games the server allows offline, with this user's download keys
const currentUserId = () => userIdFromToken(localStorage.getItem("token") || "");
const downloadedGames = () => Object.fromEntries(listOfflineGames().map((game) => [game.id, game]));

let state = {
	downloaded: downloadedGames(),
	downloadedFor: currentUserId(), // downloads belong to an account
	downloads: {},
	catalog: null,
	catalogStatus: "idle", // idle | loading | ready | error
};
const listeners = new Set();
const controllers = new Map();

const setState = (patch) => {
	state = { ...state, ...patch };
	listeners.forEach((listener) => listener());
};
const setDownload = (appId, value) => {
	const downloads = { ...state.downloads };
	if (value) downloads[appId] = value;
	else delete downloads[appId];
	setState({ downloads });
};

const subscribe = (listener) => {
	listeners.add(listener);
	return () => listeners.delete(listener);
};
// After a sign-in or sign-out, the new account's downloads.
const getSnapshot = () => {
	const userId = currentUserId();
	if (userId !== state.downloadedFor) state = { ...state, downloaded: downloadedGames(), downloadedFor: userId };
	return state;
};

if (typeof window !== "undefined") {
	window.addEventListener("akedo-offline-games-changed", () => setState({ downloaded: downloadedGames(), downloadedFor: currentUserId() }));
}

export const useOfflineGames = () => useSyncExternalStore(subscribe, getSnapshot);
export const getOfflineGamesState = getSnapshot;

// Loads which games can be downloaded (online only). Safe to call repeatedly.
let catalogPromise = null;
let catalogToken = null;
export const loadOfflineCatalog = ({ force = false } = {}) => {
	if (!isOfflinePlaySupported() || !navigator.onLine) return Promise.resolve(state.catalog);
	const token = localStorage.getItem("token") || "";
	if (catalogPromise && !force && token === catalogToken) return catalogPromise;
	catalogToken = token;
	setState({ catalogStatus: "loading" });
	catalogPromise = fetchDownloadableGames()
		.then((games) => {
			setState({ catalog: Object.fromEntries(games.map((game) => [game.id, game])), catalogStatus: "ready" });
			return state.catalog;
		})
		.catch((error) => {
			catalogPromise = null;
			setState({ catalogStatus: "error" });
			throw error;
		});
	return catalogPromise;
};

export const isDownloadable = (appId) => Boolean(state.catalog?.[appId]);

export const startOfflineDownload = async (appId) => {
	if (!isOfflinePlaySupported()) return;
	const game = state.catalog?.[appId];
	if (!game || controllers.has(appId) || state.downloaded[appId]) return;
	const controller = new AbortController();
	controllers.set(appId, controller);
	setDownload(appId, { status: "downloading", progress: 0 });
	try {
		await downloadOfflineGame(game, {
			signal: controller.signal,
			onProgress: (progress) => {
				if (!controller.signal.aborted) setDownload(appId, { status: "downloading", progress });
			},
		});
		setDownload(appId, null);
	} catch (error) {
		// A cancelled download simply goes back to "not downloaded".
		if (controller.signal.aborted) setDownload(appId, null);
		else {
			console.error("[Offline] download failed", appId, error);
			setDownload(appId, { status: "failed", error: error?.message || "Download failed" });
		}
	} finally {
		controllers.delete(appId);
	}
};

export const cancelOfflineDownload = (appId) => controllers.get(appId)?.abort();

// Removes the game's files from this device; its progress is kept.
export const removeOfflineDownload = (appId) => removeOfflineGame(appId);
